type WindowWithWebkitAudio = Window &
  typeof globalThis & {
    webkitAudioContext?: typeof AudioContext
  }

function makeImpulseResponse(
  ctx: AudioContext,
  duration: number,
  decay: number,
): AudioBuffer {
  const sampleRate = ctx.sampleRate
  const len = Math.floor(sampleRate * duration)
  const buf = ctx.createBuffer(2, len, sampleRate)
  for (let ch = 0; ch < 2; ch++) {
    const data = buf.getChannelData(ch)
    let prev = 0
    for (let i = 0; i < len; i++) {
      const t = i / len
      const env = Math.pow(1 - t, decay)
      const noise = Math.random() * 2 - 1
      // Cheap low-pass smoothing → warmer hall, less digital fizz
      const smoothed = prev * 0.55 + noise * 0.45
      prev = smoothed
      data[i] = smoothed * env
    }
  }
  return buf
}

let ctx: AudioContext | null = null
let irBuf: AudioBuffer | null = null

function getCtx(): AudioContext | null {
  if (!ctx) {
    const w = window as WindowWithWebkitAudio
    const Ctor = w.AudioContext || w.webkitAudioContext
    if (!Ctor) return null
    ctx = new Ctor()
  }
  if (ctx.state === 'suspended') {
    void ctx.resume()
  }
  return ctx
}

export function useRewardSound() {
  const playReward = () => {
    const c = getCtx()
    if (!c) return
    if (!irBuf) {
      irBuf = makeImpulseResponse(c, 3.4, 2.6)
    }
    const now = c.currentTime

    const master = c.createGain()
    master.gain.value = 0.85
    master.connect(c.destination)

    const dry = c.createGain()
    dry.gain.value = 0.42
    dry.connect(master)

    const conv = c.createConvolver()
    conv.buffer = irBuf
    const wet = c.createGain()
    wet.gain.value = 0.78
    wet.connect(conv).connect(master)

    const send = (node: AudioNode) => {
      node.connect(dry)
      node.connect(wet)
    }

    const lowVoiceShelf = c.createBiquadFilter()
    lowVoiceShelf.type = 'lowpass'
    lowVoiceShelf.frequency.value = 2400
    lowVoiceShelf.Q.value = 0.4
    send(lowVoiceShelf)

    const padFilter = c.createBiquadFilter()
    padFilter.type = 'lowpass'
    padFilter.Q.value = 0.5
    padFilter.frequency.setValueAtTime(1600, now)
    padFilter.frequency.exponentialRampToValueAtTime(3400, now + 1.4)
    send(padFilter)

    // Voice helper — detuned pair routed through a panner for stereo width
    const voice = (
      type: OscillatorType,
      freq: number,
      peak: number,
      attack: number,
      sustain: number,
      release: number,
      delay: number,
      pan: number,
      target: AudioNode,
    ) => {
      const panNode = c.createStereoPanner()
      panNode.pan.value = pan
      panNode.connect(target)

      const detunes = [-6, 6]
      for (const cents of detunes) {
        const osc = c.createOscillator()
        const g = c.createGain()
        osc.type = type
        osc.frequency.value = freq
        osc.detune.value = cents
        const t0 = now + delay
        const tEndAtt = t0 + attack
        const tEndSus = tEndAtt + sustain
        const tEnd = tEndSus + release
        g.gain.setValueAtTime(0, t0)
        g.gain.linearRampToValueAtTime(peak / detunes.length, tEndAtt)
        g.gain.linearRampToValueAtTime((peak * 0.6) / detunes.length, tEndSus)
        g.gain.exponentialRampToValueAtTime(0.0001, tEnd)
        osc.connect(g).connect(panNode)
        osc.start(t0)
        osc.stop(tEnd + 0.05)
      }
    }

    // — Grounding bass: long swell, slightly off-center
    voice('sine', 98.0, 0.085, 0.45, 1.4, 1.0, 0, -0.05, lowVoiceShelf)
    voice('sine', 196.0, 0.05, 0.45, 1.3, 1.0, 0.04, 0.05, lowVoiceShelf)

    // — Major chord pad (G/B/D/G), spread across stereo for width
    voice('triangle', 392.0, 0.052, 0.55, 1.2, 1.0, 0.18, -0.35, padFilter)
    voice('sine', 493.88, 0.044, 0.6, 1.2, 1.0, 0.22, 0.18, padFilter)
    voice('triangle', 587.33, 0.038, 0.62, 1.2, 1.1, 0.26, 0.4, padFilter)
    voice('sine', 783.99, 0.03, 0.65, 1.1, 1.2, 0.32, -0.18, padFilter)

    // — Bells: warm sine, paired soft octave for body, warmer freq range
    const bellBus = c.createBiquadFilter()
    bellBus.type = 'lowpass'
    bellBus.frequency.value = 4200
    bellBus.Q.value = 0.5
    send(bellBus)

    const bell = (
      freq: number,
      peak: number,
      delay: number,
      dur: number,
      pan: number,
    ) => {
      const panNode = c.createStereoPanner()
      panNode.pan.value = pan
      panNode.connect(bellBus)
      const t0 = now + delay

      // Fundamental
      const osc = c.createOscillator()
      const g = c.createGain()
      osc.type = 'sine'
      osc.frequency.value = freq
      g.gain.setValueAtTime(0, t0)
      g.gain.linearRampToValueAtTime(peak, t0 + 0.012)
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
      osc.connect(g).connect(panNode)
      osc.start(t0)
      osc.stop(t0 + dur + 0.05)

      // Soft sub-octave for warmth (avoids glassy/sharp top)
      const osc2 = c.createOscillator()
      const g2 = c.createGain()
      osc2.type = 'sine'
      osc2.frequency.value = freq / 2
      g2.gain.setValueAtTime(0, t0)
      g2.gain.linearRampToValueAtTime(peak * 0.35, t0 + 0.018)
      g2.gain.exponentialRampToValueAtTime(0.0001, t0 + dur * 0.9)
      osc2.connect(g2).connect(panNode)
      osc2.start(t0)
      osc2.stop(t0 + dur + 0.05)
    }

    // — Spaced bell run (slower, more contemplative). G major pentatonic ascending.
    bell(587.33, 0.06, 0.7, 1.4, -0.4) // D5
    bell(783.99, 0.058, 0.95, 1.5, 0.35) // G5
    bell(987.77, 0.052, 1.25, 1.6, -0.25) // B5
    bell(1318.51, 0.044, 1.6, 1.8, 0.3) // E6
    bell(1567.98, 0.034, 2.0, 1.8, -0.1) // G6 — soft top tail
  }

  return { playReward }
}
